import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Drawer, IconButton, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack, Typography, useMediaQuery } from "@mui/material";
import { ArrowForwardIos, Close } from "@mui/icons-material";
import { AppContext } from "../../../context/appContext";
import { sideNavigationItems } from "../../../configs/static";
import { TopNavigationNotificationIcon } from "../../../assets";
import baseLogo from "../../../assets/icons/sidenavigationappicon.svg";
import { BaseDropDownType } from "../../../types/component.type";
import { NavigationPropsType } from "../../../types/container.type";
import { BaseDropDown } from "../../../components/modal/dropdown";
import { SideNavigationMenu } from "../../../components/menu/sidenavigation";

export const SideNavigation: React.FC<NavigationPropsType> = ({ username, role, logo, headshot, handleLogOutUser }) => {
    const noSubNavItems = ["Dashboard"];
    const userMenuItems = [
        {
            title: "Change Password",
            url: "/passwordreset",
        },
        {
            title: "Settings",
            url: "/profile",
        },
        {
            title: "Log Out",
            url: "/logout",
        }
    ];

    const navigate = useNavigate();
    const matchesMobileAndAbove = useMediaQuery('(min-width:425px)');
    const matchesMobileAndBelow = useMediaQuery('(max-width:768px)');
    const {
        activeNavItem, setActiveNavItem, setIsSideNavigationClosing, isMobileSideNavigationOpen, setIsMobileSideNavigationOpen
    } = useContext(AppContext);

    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

    const handleDrawerClose = () => {
        setIsSideNavigationClosing(true);
        setIsMobileSideNavigationOpen(false);
    };

    const handleDrawerTransitionEnd = () => {
        setIsSideNavigationClosing(false);
    };

    const handleNavItemClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, item: string) => {
        e.stopPropagation();
        if (noSubNavItems.includes(item)) {
            navigate(`/${item.toLowerCase()}`);
            return setIsMobileSideNavigationOpen(false);
        }
        // reset active nav item to default and toggle close dropdown
        if (item === activeNavItem) {
            return setActiveNavItem(undefined);
        }
        return setActiveNavItem(item);
    };

    const handleUserMenuClick = () => {
        return setIsUserMenuOpen(true);
    };

    const handleUserMenuClose = () => {
        return setIsUserMenuOpen(false);
    };

    const handleUserMenuItemClick = (e: React.MouseEvent<HTMLLIElement, MouseEvent>, item: BaseDropDownType) => {
        e.preventDefault();
        switch (item.title) {
            case "Change Password":
            case "Settings":
                navigate(item.url);
                setIsUserMenuOpen(false);
                break;
            case "Log Out":
                handleLogOutUser();
                setIsUserMenuOpen(false);
                break;
            default:
                break;
        }
    };

    const drawer = (
        sideNavigationItems.map((sideNavItem, index) => {
            return (
                <ListItem
                    key={index}
                    className={`${sideNavItem.name.replace(/\s+/g, '-')}-Side-Nav-Item Side-Nav-Item`}
                    component={"div"}
                    sx={{
                        flexDirection: "column",
                        alignItems: "stretch",
                        padding: "calc(var(--basic-padding)/4) calc(var(--basic-padding)/2)",
                        marginTop: (index === 0) ? "calc(var(--basic-margin)/8)" : "0",
                    }}
                    onClick={(e) => handleNavItemClick(e, sideNavItem.name)}
                >
                    <ListItemButton>
                        <ListItemIcon>
                            {sideNavItem.icon}
                        </ListItemIcon>
                        <ListItemText
                            primary={sideNavItem.name}
                        />
                        <ListItemIcon
                            sx={{
                                display: (index === 0) ? "none" : "inline-flex",
                                justifyContent: "flex-end"
                            }}
                            className="arrow-box"
                        >
                            <ArrowForwardIos sx={{ color: "var(--light-color)" }} />
                        </ListItemIcon>
                    </ListItemButton>
                    {(!noSubNavItems.includes(sideNavItem.name)) && (
                        <SideNavigationMenu
                            open={activeNavItem === sideNavItem.name}
                            menuitems={sideNavItem.subItems}
                        />
                    )}
                </ListItem>
            )
        })
    );

    return (
        <Box
            component={"nav"}
            sx={{ width: { mobile: "var(--side-nav-width)" }, flexShrink: { mobile: 0 } }}
        >
            <Drawer
                variant="temporary"
                open={isMobileSideNavigationOpen}
                onTransitionEnd={handleDrawerTransitionEnd}
                onClose={handleDrawerClose}
                ModalProps={{
                    keepMounted: true
                }}
                sx={{
                    display: { mobile: 'block', tablet: 'none' },
                    '& .MuiDrawer-paper': {
                        boxSizing: 'border-box',
                        width: "var(--side-nav-width)",
                        backgroundColor: "var(--primary-color)",
                        paddingBottom: "calc(var(--basic-padding) / 2)"
                    },
                }}
            >
                <ListItem
                    className="side-navigation-header"
                >
                    <ListItemIcon
                        className="app-icon"
                    >
                        <img
                            src={logo || baseLogo}
                            alt="institution-logo"
                            className="institution-logo"
                        />
                    </ListItemIcon>
                    <Box
                        component={"div"}
                        className="close-button"
                    >
                        <IconButton
                            sx={{
                                color: "#FAF6F6",
                                border: "1px solid #FAF6F6",
                                borderRadius: "50%",
                                display: matchesMobileAndAbove ? "none" : "inline-flex"
                            }}
                            onClick={handleDrawerClose}
                        >
                            <Close />
                        </IconButton>
                    </Box>
                </ListItem>
                {drawer}
                <BaseDropDown
                    open={(matchesMobileAndBelow && isUserMenuOpen)}
                    className="side-navigation-user-menu-dropdown user-menu-dropdown"
                    handleClose={handleUserMenuClose}
                    items={userMenuItems}
                    handleItemClick={handleUserMenuItemClick}
                />
                <Box
                    borderTop={"2px solid var(--light-color)"}
                    marginTop={"var(--basic-margin)"}
                >
                    <Stack
                        direction={"row"}
                        alignItems={"center"}
                        gap={"var(--flex-gap)"}
                        className="notification-icon-area"
                    >
                        <Box
                            overflow={"hidden"}
                        >
                            <IconButton
                                sx={{ padding: 0 }}
                            >
                                <TopNavigationNotificationIcon />
                            </IconButton>
                        </Box>
                        <Box
                            overflow={"hidden"}
                        >
                            <Typography
                                variant="subtitle1"
                                fontFamily={"Poppins"}
                                fontWeight={700}
                                fontSize={20}
                                lineHeight={"normal"}
                                color="var(--light-color)"
                            >
                                See Notifications
                            </Typography>
                        </Box>
                    </Stack>
                    <Stack
                        className="user-profile-icon-area"
                        direction={"row"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                        gap={"var(--flex-gap)"}
                        onClick={handleUserMenuClick}
                    >
                        <Stack
                            direction={"row"}
                            alignItems={"center"}
                            gap={"var(--flex-gap)"}
                            overflow={"hidden"}
                        >
                            {headshot}
                            <Box
                                overflow={"hidden"}
                            >
                                <Typography
                                    variant="subtitle1"
                                    fontFamily={"Poppins"}
                                    fontWeight={700}
                                    fontSize={20}
                                    lineHeight={"normal"}
                                    color="var(--light-color)"
                                >
                                    {username}
                                </Typography>
                            </Box>
                        </Stack>
                        <Stack
                            direction={"row"}
                            alignItems={"center"}
                            gap={"calc(var(--flex-gap)/4)"}
                            overflow={"hidden"}
                        >
                            <Box
                                component={"div"}
                                className="role-box"
                            >
                                <Typography
                                    variant="subtitle1"
                                    fontFamily={"Inter"}
                                    fontWeight={400}
                                    fontSize={14}
                                    lineHeight={"normal"}
                                    maxWidth={"2.6765625rem"}
                                    sx={{
                                        background: "linear-gradient(92deg, #0058FA 1.48%, #0058FA 92.76%)",
                                        backgroundClip: "text",
                                        textFillColor: "transparent",
                                    }}
                                >
                                    {role}
                                </Typography>
                            </Box>
                            <IconButton
                                sx={{ padding: 0 }}
                            >
                                <ArrowForwardIos sx={{ color: "var(--light-color)", transform: "rotate(90deg)" }} />
                            </IconButton>
                        </Stack>
                    </Stack>
                </Box>
            </Drawer>
            <Drawer
                variant="permanent"
                sx={{
                    display: { mobile: 'none', tablet: 'block' },
                    '& .MuiDrawer-paper': {
                        boxSizing: 'border-box',
                        width: "var(--side-nav-width)",
                        backgroundColor: "var(--primary-color)",
                        paddingBottom: "calc(var(--basic-padding) / 2)"
                    },
                }}
                open
            >
                <ListItem
                    className="side-navigation-header"
                >
                    <ListItemIcon
                        className="app-icon"
                    >
                        {logo ?
                            (<img
                                src={logo}
                                alt="institution-logo"
                                className="institution-logo"
                            />
                            ) : (
                                <CircularProgress color="inherit" className="loader institution-logo" style={{ width: "93px !important", height: "110px !important" }} />
                            )}
                    </ListItemIcon>
                </ListItem>
                {drawer}
            </Drawer>
        </Box>
    )
}
